import React, { FC } from 'react';
import {
  ApproveFitupError,
  ApproveWeldError,
  CommodityWrapper,
  UnApproveWeldError,
  UnclaimWeldError,
  UnApproveFitupError,
} from '@bechtel/types';
import {
  Box,
  Text,
  Button,
  ButtonAppearance,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalSize,
  ModalClose,
  Spinner,
} from '@bechtel/gds';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { t, Trans } from '@lingui/macro';
import { WeldActionErrorProps } from './types';
import { GET_COMMODITIES_BY_COMMODITY_IDS } from '@bechtel/client-gql';

const getErrorMessage = (error: WeldActionErrorProps['error']) => {
  if (error instanceof ApolloError) {
    return <Trans>Network error has occured.Please close button and try again.</Trans>;
  }
  switch (error) {
    case UnclaimWeldError.UNCLAIM_WELD_MILESTONE_ERRORED:
      return <Trans>Unclaim Weld milestone action errored.</Trans>;
    case UnclaimWeldError.EIS_ERROR:
    case UnApproveWeldError.EIS_ERROR:
      return <Trans>EIS error.</Trans>;
    case UnclaimWeldError.MILESTONE_CLAIMED_BY_SOME_OTHER_USER:
      return <Trans>Weld claimed by some other user.</Trans>;
    case UnclaimWeldError.MILESTONE_CONFIG_NOT_FOUND:
    case UnApproveWeldError.MILESTONE_CONFIG_NOT_FOUND:
      return <Trans>Weld config not found.</Trans>;
    case UnclaimWeldError.MILESTONE_NOT_CLAIMED:
      return <Trans>Weld is not claimed.</Trans>;
    case UnclaimWeldError.MILESTONE_IS_APPROVED:
      return <Trans>Weld is approved.</Trans>;
    case UnclaimWeldError.MILESTONE_IS_BLOCKED:
    case UnApproveWeldError.MILESTONE_IS_BLOCKED:
      return <Trans>Weld is blocked.</Trans>;
    case UnclaimWeldError.MILESTONE_IS_REJECTED:
    case UnApproveWeldError.MILESTONE_IS_REJECTED:
      return <Trans>Weld is rejected.</Trans>;
    case UnclaimWeldError.MISSING_REQUIRED_PERMISSION:
    case ApproveWeldError.MISSING_REQUIRED_PERMISSION:
    case UnApproveWeldError.MISSING_REQUIRED_PERMISSION:
      return <Trans>Missing required permission.</Trans>;
    case UnclaimWeldError.NOT_FOUND:
    case UnApproveWeldError.NOT_FOUND:
      return <Trans>Not Found.</Trans>;
    case UnclaimWeldError.SERVICE_PROXY_ERROR:
    case UnApproveWeldError.SERVICE_PROXY_ERROR:
      return <Trans>Server proxy error.</Trans>;
    case ApproveWeldError.INVALID_WELD:
      return <Trans>Invalid Weld.</Trans>;
    case UnApproveWeldError.MILESTONE_IS_NOT_APPROVED:
      return <Trans>Weld is not approved.</Trans>;
    case UnApproveWeldError.MILESTONE_APPROVED_BY_SOME_OTHER_USER:
      return <Trans>Weld approved by some other user.</Trans>;
    case ApproveWeldError.APPROVE_WELD_MILESTONE_ERRORED:
      return <Trans>Apporve weld action errored.</Trans>;
    case UnApproveWeldError.UNAPPROVE_WELD_MILESTONE_ERRORED:
      return <Trans>UnApporve weld action errored.</Trans>;
    case ApproveFitupError.APPROVE_FITUP_MILESTONE_ERRORED:
      return <Trans>Approve Fitup milestone action errored.</Trans>;
    case UnApproveFitupError.UNAPPROVE_FITUP_MILESTONE_ERRORED:
      return <Trans>UnApprove Fitup milestone action errored.</Trans>;
    case ApproveWeldError.NO_WELDER_FOUND:
      return <Trans>Welder is not assigned on weld</Trans>;
    default:
      return <Trans>An error has occured. Please click close button and try again.</Trans>;
  }
};

export const WeldActionError: FC<WeldActionErrorProps> = ({ error, close, commodityId }) => {
  const [getCommoditiesByIds, { loading }] = useLazyQuery<{ commodities: CommodityWrapper[] }, { ids: string[] }>(
    GET_COMMODITIES_BY_COMMODITY_IDS,
    {
      variables: {
        ids: [commodityId],
      },
      fetchPolicy: 'network-only',
    }
  );
  return (
    <Modal visible size={ModalSize.DEFAULT} style={{ width: '400px', height: '275px' }}>
      <ModalHeader title={t`Weld Action Error`}>
        <ModalClose onClick={close}></ModalClose>
      </ModalHeader>
      <ModalBody>
        <Box p={{ sm: '500' }}>
          <Text size="500">{getErrorMessage(error)}</Text>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Box mr={{ sm: '400' }}>
            <Button appearance={ButtonAppearance.SECONDARY} onClick={close}>
              <Trans> Close</Trans>
            </Button>
          </Box>
          <Box>
            <Button
              type="submit"
              appearance={ButtonAppearance.SECONDARY}
              onClick={async () => {
                await getCommoditiesByIds();
                await close();
              }}
              disabled={loading}
            >
              {loading && <Spinner></Spinner>}
              {!loading && error instanceof ApolloError ? t`Retry` : t`Update`}
            </Button>
          </Box>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
